import React, { useEffect, useState } from 'react'
import { GetAction } from '../../Component/GetAction'
import { PostFormAction } from '../../Component/PostFormAction'
import { RequireModule, PageHeader } from './shared'
import { mediaUrl } from '../../utils/mediaUrl'

const EMPTY = {
  website_name: '',
  footer_address: '',
  footer_contact: '',
  receipt_name: '',
  receipt_address: '',
  receipt_registration_number: '',
  receipt_website: '',
  logo_path: '',
}

function SettingsPage() {
  const [form, setForm] = useState(EMPTY)
  const [logoFile, setLogoFile] = useState(null)
  const [preview, setPreview] = useState('')
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const data = await GetAction('get', {}, 'settings/get')
      if (cancelled) return
      if (data?.status === 'success' && data.response) {
        const r = data.response
        setForm({
          website_name: r.website_name || '',
          footer_address: r.footer_address || '',
          footer_contact: r.footer_contact || '',
          receipt_name: r.receipt_name || r.website_name || '',
          receipt_address: r.receipt_address || '',
          receipt_registration_number: r.receipt_registration_number || '',
          receipt_website: r.receipt_website || '',
          logo_path: r.logo_path || '',
        })
        setPreview(mediaUrl(r.logo_path || ''))
        setMessage(null)
      } else {
        setMessage({ error: true, text: data?.message || 'Failed to load settings.' })
      }
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const onChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const onLogo = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setLogoFile(file)
    setPreview(URL.createObjectURL(file))
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setMessage(null)
    const fd = new FormData()
    fd.append('website_name', form.website_name.trim())
    fd.append('footer_address', form.footer_address.trim())
    fd.append('footer_contact', form.footer_contact.trim())
    fd.append('receipt_name', form.receipt_name.trim())
    fd.append('receipt_address', form.receipt_address.trim())
    fd.append('receipt_registration_number', form.receipt_registration_number.trim())
    fd.append('receipt_website', form.receipt_website.trim())
    if (logoFile) fd.append('logo', logoFile)

    const data = await PostFormAction('post', fd, 'settings/save')
    if (data?.status === 'success') {
      setMessage({ error: false, text: data.message || 'Settings saved.' })
      if (data.response?.logo_path) {
        setPreview(mediaUrl(data.response.logo_path))
        setForm((prev) => ({ ...prev, logo_path: data.response.logo_path }))
      }
      setLogoFile(null)
    } else {
      setMessage({ error: true, text: data?.message || 'Failed to save settings.' })
    }
    setSaving(false)
  }

  return (
    <RequireModule moduleKey="settings">
      <PageHeader
        title="Settings"
        subtitle="Website, footer, and donation receipt details."
      />
      {message ? (
        <div
          className={`manage-alert ${message.error ? 'manage-alert-err' : 'manage-alert-ok'}`}
        >
          {message.text}
        </div>
      ) : null}
      <div className="manage-card">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-warning" role="status" />
          </div>
        ) : (
          <form onSubmit={onSubmit}>
            <div className="mb-3">
              <label className="form-label" htmlFor="website_name">
                Website Name
              </label>
              <input
                id="website_name"
                name="website_name"
                className="form-control"
                required
                value={form.website_name}
                onChange={onChange}
              />
              <div className="form-text">Shown in the site header and footer brand area</div>
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="logo">
                Website Logo
              </label>
              <input
                id="logo"
                type="file"
                accept="image/*"
                className="form-control"
                onChange={onLogo}
              />
              <div className="form-text">Leave empty to keep the current logo.</div>
              {preview ? (
                <img
                  src={preview}
                  alt="Logo preview"
                  className="mt-2"
                  style={{
                    maxHeight: 100,
                    maxWidth: 220,
                    objectFit: 'contain',
                    background: '#fafafa',
                    border: '1px solid #ddd',
                    padding: 8,
                    borderRadius: 4,
                  }}
                />
              ) : null}
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="footer_address">
                Footer Address
              </label>
              <textarea
                id="footer_address"
                name="footer_address"
                className="form-control"
                rows={3}
                required
                value={form.footer_address}
                onChange={onChange}
              />
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="footer_contact">
                Footer Contact Number
              </label>
              <input
                id="footer_contact"
                name="footer_contact"
                className="form-control"
                required
                value={form.footer_contact}
                onChange={onChange}
              />
            </div>

            <hr />
            <h2 className="h5">Donation Receipt</h2>
            <p className="text-muted small">
              Details printed at the top of payment receipts.
            </p>

            <div className="mb-3">
              <label className="form-label" htmlFor="receipt_name">
                Receipt Name
              </label>
              <input
                id="receipt_name"
                name="receipt_name"
                className="form-control"
                required
                value={form.receipt_name}
                onChange={onChange}
              />
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="receipt_address">
                Receipt Address
              </label>
              <textarea
                id="receipt_address"
                name="receipt_address"
                className="form-control"
                rows={3}
                required
                value={form.receipt_address}
                onChange={onChange}
              />
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="receipt_registration_number">
                Registration Number
              </label>
              <input
                id="receipt_registration_number"
                name="receipt_registration_number"
                className="form-control"
                required
                value={form.receipt_registration_number}
                onChange={onChange}
              />
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="receipt_website">
                Website
              </label>
              <input
                id="receipt_website"
                name="receipt_website"
                className="form-control"
                required
                value={form.receipt_website}
                onChange={onChange}
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              style={{ backgroundColor: '#fd7e14', borderColor: '#fd7e14' }}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </form>
        )}
      </div>
    </RequireModule>
  )
}

export default SettingsPage
